<template>
  <div class="careers-root">

    <!-- ── Nav bar ─────────────────────────────────────────────────── -->
    <nav class="careers-nav">
      <div class="nav-inner">
        <div class="nav-brand">
          <img v-if="logoUrl" :src="logoUrl" class="nav-logo" alt="Company logo" />
          <img v-else :src="projectLogo" class="nav-logo" alt="Mapiibs" />
          <span class="nav-name">{{ main_company.company_name || 'Careers' }}</span>
        </div>
        <span class="nav-tag">Open Positions</span>
      </div>
    </nav>

    <!-- ── Hero ───────────────────────────────────────────────────── -->
    <section class="hero">
      <div class="hero-inner">
        <p class="hero-eyebrow">We're hiring</p>
        <h1 class="hero-title">Build the future <span class="hero-accent">with us.</span></h1>
        <p class="hero-sub">
          Explore open roles at {{ company.name || 'our company' }} and find the one that's right for you.
        </p>

        <!-- Search bar -->
        <div class="search-bar">
          <div class="search-input-wrap">
            <svg class="search-icon" viewBox="0 0 20 20" fill="none"><circle cx="9" cy="9" r="6" stroke="currentColor" stroke-width="1.8"/><path d="M15 15l-2.5-2.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
            <input type="text" class="search-input" v-model="filters.search" placeholder="Search roles, keywords…" @keyup.enter="fetchJobs" />
          </div>
          <select class="search-select" v-model="filters.type" @change="fetchJobs">
            <option value="">All Types</option>
            <option value="permanent">Permanent</option>
            <option value="contract">Contract</option>
            <option value="intern">Internship</option>
            <option value="adhoc">Ad-hoc</option>
          </select>
          <button class="search-btn" @click="fetchJobs">Search</button>
        </div>
      </div>
    </section>

    <!-- ── Job List ────────────────────────────────────────────────── -->
    <main class="jobs-main">
      <div class="jobs-inner">

        <!-- Stats bar -->
        <div class="stats-bar" v-if="!loading">
          <p class="stats-text"><strong>{{ totalJobs }}</strong> open position{{ totalJobs !== 1 ? 's' : '' }}</p>
        </div>

        <!-- Skeleton -->
        <div v-if="loading" class="jobs-grid">
          <div v-for="n in 6" :key="n" class="job-card-sk">
            <div class="sk-line sk-line--title"></div>
            <div class="sk-line sk-line--sm"></div>
            <div class="sk-line sk-line--sm" style="width:55%"></div>
            <div class="sk-line sk-line--btn"></div>
          </div>
        </div>

        <!-- Job Cards -->
        <div v-else-if="jobs.length" class="jobs-grid">
          <article v-for="job in jobs" :key="job.id" class="job-card" @click="goToJob(job)">
            <div class="job-card-body">
              <div class="job-card-top">
                <span class="job-type-badge" :class="typeClass(job.requisition?.employment_type || '')">
                  {{ typeLabel(job.requisition?.employment_type || 'Open') }}
                </span>
                <span v-if="job.application_deadline" class="job-deadline" :class="{ 'job-deadline--soon': isSoon(job.application_deadline) }">
                  <svg viewBox="0 0 16 16" fill="none" width="12" height="12"><circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.5"/><path d="M8 5v3.5l2 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                  Closes {{ fmtDate(job.application_deadline) }}
                </span>
              </div>
              <h3 class="job-title">{{ job.title }}</h3>
              <div class="job-meta">
                <span v-if="job.requisition?.department?.name" class="job-meta-item">
                  <svg viewBox="0 0 16 16" fill="none" width="13" height="13"><rect x="2" y="4" width="12" height="9" rx="1.5" stroke="currentColor" stroke-width="1.4"/><path d="M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1" stroke="currentColor" stroke-width="1.4"/></svg>
                  {{ job.requisition.department.name }}
                </span>
                <span v-if="job.requisition?.location || job.requisition?.company_location" class="job-meta-item">
                  <svg viewBox="0 0 16 16" fill="none" width="13" height="13"><path d="M8 1.5C5.515 1.5 3.5 3.515 3.5 6c0 3.5 4.5 8.5 4.5 8.5S12.5 9.5 12.5 6C12.5 3.515 10.485 1.5 8 1.5z" stroke="currentColor" stroke-width="1.4"/><circle cx="8" cy="6" r="1.5" stroke="currentColor" stroke-width="1.4"/></svg>
                  {{ job.requisition?.company_location?.name || job.requisition?.location || 'Remote' }}
                </span>
              </div>
              <p v-if="job.description" class="job-excerpt">{{ excerpt(job.description) }}</p>
            </div>
            <div class="job-card-footer">
              <router-link :to="jobUrl(job)" class="job-cta" @click.stop>
                View & Apply <svg viewBox="0 0 16 16" fill="none" width="14" height="14"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </router-link>
              <button class="job-share-btn" @click.stop="copyLink(job)" :title="copied === job.id ? 'Copied!' : 'Copy link'">
                <svg v-if="copied !== job.id" viewBox="0 0 16 16" fill="none" width="14" height="14"><rect x="5.5" y="1.5" width="9" height="11" rx="1.5" stroke="currentColor" stroke-width="1.4"/><rect x="1.5" y="4.5" width="9" height="11" rx="1.5" stroke="currentColor" stroke-width="1.4"/></svg>
                <svg v-else viewBox="0 0 16 16" fill="none" width="14" height="14"><path d="M3 8l3 3 7-7" stroke="#16a34a" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                {{ copied === job.id ? 'Copied!' : 'Share' }}
              </button>
            </div>
          </article>
        </div>

        <!-- Empty -->
        <div v-else class="empty-state">
          <div class="empty-icon">
            <svg viewBox="0 0 64 64" fill="none" width="64" height="64"><rect x="8" y="12" width="48" height="40" rx="6" stroke="#dde5f0" stroke-width="2.5"/><path d="M20 28h24M20 36h16" stroke="#dde5f0" stroke-width="2.5" stroke-linecap="round"/></svg>
          </div>
          <h3 class="empty-title">No open positions right now</h3>
          <p class="empty-sub">Check back soon — we're always looking for great people.</p>
        </div>

        <!-- Pagination -->
        <div v-if="meta.last_page > 1" class="pagination">
          <button class="page-btn" :disabled="meta.current_page === 1" @click="goPage(meta.current_page - 1)">‹ Prev</button>
          <span class="page-info">Page {{ meta.current_page }} of {{ meta.last_page }}</span>
          <button class="page-btn" :disabled="meta.current_page === meta.last_page" @click="goPage(meta.current_page + 1)">Next ›</button>
        </div>

      </div>
    </main>

    <!-- ── Footer ─────────────────────────────────────────────────── -->
    <footer class="careers-footer">
      <div class="footer-inner">
        <p class="footer-company">{{ main_company.company_name }}</p>
        <p class="footer-copy">© {{ year }} All rights reserved.</p>
      </div>
    </footer>

  </div>
</template>

<script>
import axios from 'axios';
import host from '../main';
import projectLogo from '../private/private-assets/images/logo.png';

export default {
  name: 'JobListings',

  data() {
    return {
      loading:   true,
      jobs:      [],
      totalJobs: 0,
      meta:      { current_page: 1, last_page: 1 },
      company:   {},
      main_company:   {},
      filters:   { search: '', type: '', page: 1, length: 12 },
      copied:      null,
      hostName:    host.hostName,
      projectLogo: projectLogo,
    };
  },

  computed: {
    companyCode() { return this.$route?.params?.companyCode; },
    year()        { return new Date().getFullYear(); },
    logoUrl()     { return this.main_company.logo ? this.hostName + this.main_company.logo : null; },
    companyInitials() {
      const name = this.main_company.company_name || '';
      return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
    },
  },

  mounted() {
    this.fetchJobs();
  },

  methods: {
    async fetchJobs() {
      this.loading = true;
      try {
        const { data } = await axios.get(`/public/careers/${this.companyCode}`, { params: this.filters });
        const payload = data.data || {};
        const paged   = payload.postings || {};
        this.jobs      = paged.data || [];
        this.totalJobs = paged.total || this.jobs.length;
        this.meta      = { current_page: paged.current_page || 1, last_page: paged.last_page || 1 };
        this.company   = payload.business || {};
        this.main_company   = payload.company || {};
      } catch (e) {
        this.jobs = [];
      } finally {
        this.loading = false;
      }
    },

    goPage(p) { this.filters.page = p; this.fetchJobs(); },

    goToJob(job) {
      this.$router.push({ name: 'JobDetail', params: { companyCode: this.companyCode, slug: job.slug } });
    },

    jobUrl(job) {
      return { name: 'JobDetail', params: { companyCode: this.companyCode, slug: job.slug } };
    },

    async copyLink(job) {
      const url = `${window.location.origin}/careers/${this.companyCode}/${job.slug}`;
      try { await navigator.clipboard.writeText(url); } catch { /* fallback */ }
      this.copied = job.id;
      setTimeout(() => { this.copied = null; }, 2200);
    },

    // Helpers
    typeLabel(t) {
      return { permanent: 'Full-Time', contract: 'Contract', intern: 'Internship', adhoc: 'Ad-hoc' }[t] || t || 'Open';
    },
    typeClass(t) {
      return { permanent: 'badge--green', contract: 'badge--blue', intern: 'badge--purple', adhoc: 'badge--amber' }[t] || 'badge--gray';
    },
    fmtDate(d) {
      return d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
    },
    isSoon(d) {
      if (!d) return false;
      const diff = new Date(d) - new Date();
      return diff > 0 && diff < 7 * 24 * 3600 * 1000;
    },
    excerpt(text, len = 120) {
      if (!text) return '';
      const plain = text.replace(/<[^>]+>/g, '');
      return plain.length > len ? plain.slice(0, len).trim() + '…' : plain;
    },
  },
};
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; }

.careers-root { min-height: 100vh; background: #f8fafc; font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; }

/* ── Nav ── */
.careers-nav { background: #fff; border-bottom: 1px solid #e8edf5; position: sticky; top: 0; z-index: 50; }
.nav-inner   { max-width: 1100px; margin: 0 auto; padding: 0 24px; height: 60px; display: flex; align-items: center; justify-content: space-between; }
.nav-brand   { display: flex; align-items: center; gap: 10px; }
.nav-logo    { height: 36px; width: auto; max-width: 160px; object-fit: contain; }
.nav-logo-initials { width: 32px; height: 32px; border-radius: 6px; background: #004AAD; color: #fff; font-size: 13px; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.nav-name    { font-size: 17px; font-weight: 800; color: #0f172a; letter-spacing: -0.3px; }
.nav-tag     { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #7a90b0; }

/* ── Hero ── */
.hero       { background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #004AAD 100%); padding: 64px 24px 80px; position: relative; overflow: hidden; }
.hero::before { content: ''; position: absolute; inset: 0; background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='20'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E"); }
.hero-inner { max-width: 680px; margin: 0 auto; text-align: center; position: relative; }

.hero-logo-wrap    { margin: 0 0 24px; display: flex; justify-content: flex-start; }
.hero-logo         { height: 72px; width: auto; max-width: 240px; object-fit: contain; border-radius: 14px; background: #fff; padding: 10px 18px; box-shadow: 0 4px 24px rgba(0,0,0,.25); }
.hero-logo-initials{ width: 80px; height: 80px; border-radius: 16px; background: rgba(255,255,255,.15); border: 2px solid rgba(255,255,255,.25); color: #fff; font-size: 28px; font-weight: 800; display: flex; align-items: center; justify-content: center; letter-spacing: -1px; backdrop-filter: blur(8px); }

.hero-eyebrow { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: #60a5fa; margin: 0 0 12px; }
.hero-title   { font-size: clamp(32px, 5vw, 52px); font-weight: 800; color: #fff; line-height: 1.12; margin: 0 0 16px; letter-spacing: -1px; }
.hero-accent  { color: #60a5fa; }
.hero-sub     { font-size: 17px; color: #94a3b8; margin: 0 0 36px; line-height: 1.6; }

/* Search bar */
.search-bar        { display: flex; gap: 10px; max-width: 600px; margin: 0 auto; }
.search-input-wrap { flex: 1; position: relative; }
.search-icon       { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; }
.search-input      { width: 100%; height: 46px; padding: 0 16px 0 40px; border: 1.5px solid rgba(255,255,255,.12); border-radius: 10px; background: rgba(255,255,255,.1); color: #fff; font-size: 14px; outline: none; backdrop-filter: blur(8px); transition: border-color .15s; }
.search-input::placeholder { color: rgba(255,255,255,.45); }
.search-input:focus { border-color: #60a5fa; }
.search-select { height: 46px; padding: 0 14px; border: 1.5px solid rgba(255,255,255,.12); border-radius: 10px; background: rgba(255,255,255,.1); color: #fff; font-size: 13px; outline: none; cursor: pointer; backdrop-filter: blur(8px); }
.search-select option { color: #1a2b45; background: #fff; }
.search-btn    { height: 46px; padding: 0 22px; border-radius: 10px; background: #004AAD; color: #fff; font-size: 14px; font-weight: 700; border: none; cursor: pointer; white-space: nowrap; transition: background .15s; }
.search-btn:hover { background: #60a5fa; }

/* ── Main ── */
.jobs-main  { max-width: 1100px; margin: 0 auto; padding: 40px 24px 64px; }
.jobs-inner { }

.stats-bar  { margin-bottom: 20px; }
.stats-text { font-size: 14px; color: #64748b; margin: 0; }
.stats-text strong { color: #0f172a; }

/* Job grid */
.jobs-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 18px; }

/* Skeleton cards */
.job-card-sk { background: #fff; border: 1px solid #e8edf5; border-radius: 14px; padding: 22px 20px; }
.sk-line      { background: linear-gradient(90deg, #f0f4f8 25%, #e4eaf2 50%, #f0f4f8 75%); background-size: 200% 100%; animation: shimmer 1.4s infinite; border-radius: 6px; margin-bottom: 10px; height: 14px; }
.sk-line--title { height: 20px; width: 70%; }
.sk-line--sm    { height: 12px; width: 45%; }
.sk-line--btn   { height: 36px; width: 100%; margin-top: 16px; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* Job cards */
.job-card      { background: #fff; border: 1px solid #e8edf5; border-radius: 14px; display: flex; flex-direction: column; cursor: pointer; transition: box-shadow .18s, transform .18s, border-color .18s; overflow: hidden; }
.job-card:hover { box-shadow: 0 8px 28px rgba(0,74,173,.1); transform: translateY(-2px); border-color: #c7d9f5; }
.job-card-body { padding: 22px 20px 14px; flex: 1; }
.job-card-top  { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.job-type-badge { display: inline-flex; align-items: center; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; letter-spacing: 0.03em; }
.badge--green  { background: #dcfce7; color: #15803d; }
.badge--blue   { background: #dbeafe; color: #1d4ed8; }
.badge--purple { background: #ede9fe; color: #7c3aed; }
.badge--amber  { background: #fef3c7; color: #b45309; }
.badge--gray   { background: #f1f5f9; color: #475569; }

.job-deadline { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; color: #94a3b8; }
.job-deadline--soon { color: #f59e0b; font-weight: 600; }
.job-title    { font-size: 17px; font-weight: 700; color: #0f172a; margin: 0 0 10px; line-height: 1.3; letter-spacing: -0.2px; }
.job-meta     { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 12px; }
.job-meta-item{ display: inline-flex; align-items: center; gap: 4px; font-size: 12px; color: #64748b; }
.job-excerpt  { font-size: 13px; color: #64748b; line-height: 1.55; margin: 0; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }

.job-card-footer { border-top: 1px solid #f0f4f8; padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.job-cta      { display: inline-flex; align-items: center; gap: 6px; color: #004AAD; font-size: 13px; font-weight: 700; text-decoration: none; transition: gap .15s; }
.job-cta:hover { gap: 10px; }
.job-share-btn { display: inline-flex; align-items: center; gap: 5px; padding: 5px 10px; border-radius: 7px; border: 1px solid #e8edf5; background: #f8fafc; font-size: 12px; color: #64748b; cursor: pointer; transition: background .15s; }
.job-share-btn:hover { background: #f0f4f8; }

/* Empty */
.empty-state { text-align: center; padding: 80px 20px; }
.empty-icon  { margin: 0 auto 16px; display: inline-block; }
.empty-title { font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 8px; }
.empty-sub   { font-size: 14px; color: #94a3b8; margin: 0; }

/* Pagination */
.pagination { display: flex; align-items: center; justify-content: center; gap: 16px; margin-top: 40px; }
.page-btn   { padding: 8px 20px; border-radius: 9px; border: 1px solid #e8edf5; background: #fff; font-size: 13px; font-weight: 600; color: #0f172a; cursor: pointer; transition: background .15s; }
.page-btn:hover:not(:disabled) { background: #f0f7ff; border-color: #93c5fd; color: #004AAD; }
.page-btn:disabled { opacity: .4; cursor: not-allowed; }
.page-info  { font-size: 13px; color: #64748b; }

/* Footer */
.careers-footer { background: #0f172a; padding: 32px 24px; }
.footer-inner   { max-width: 1100px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.footer-company { font-size: 14px; font-weight: 700; color: #fff; margin: 0; }
.footer-copy    { font-size: 12px; color: #475569; margin: 0; }

@media (max-width: 640px) {
  .search-bar     { flex-direction: column; }
  .search-select  { width: 100%; }
  .search-btn     { width: 100%; }
  .jobs-grid      { grid-template-columns: 1fr; }
}
</style>
